import React, { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import Sidebar from "../../Components/Sidebar";
import Topbar from "../../Components/Topbar";
import {
  getAssistantOverview,
  getConversationTimeseries,
  getConversationTopics,
  listConversations,
} from "../../actions/assistants";
import { readSeries, readTopics } from "../Analytics/mapAnalytics";
import { mapConversations } from "../Conversations/mapConversation";
import { RobotIcon } from "../Auth/onboarding/icons";
import { CalendarIcon, ShareIcon, ChevronDownIcon } from "./icons";
import StatCards from "./StatCards";
// The same bar chart the Analytics page draws: both read the timeseries
// endpoint, so there is no reason for two chart implementations.
import ConversationsChart from "../Analytics/ConversationsChart";
import AssistantLiveCard from "./AssistantLiveCard";
import TopTopics from "./TopTopics";
import RecentConversations from "./RecentConversations";
import QuickActions from "./QuickActions";
// import ImproveBanner from "./ImproveBanner";
import { mapOverview } from "./mapOverview";
import LoadingState from "../../Components/LoadingState";

const RANGES = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 14 days", days: 14 },
  { label: "Last 30 days", days: 30 },
];

// The API takes plain YYYY-MM-DD, so this stays in local time rather than
// going through toISOString() and shifting the day across a timezone.
const toApiDate = (date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const shiftDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

// `days` back from today, inclusive of today.
const rangeFor = (days) => {
  const to = new Date();
  return { from: toApiDate(shiftDays(to, -(days - 1))), to: toApiDate(to) };
};

const prettyDate = (value) =>
  new Date(value).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
  });

const prettyRange = ({ from, to }) => `${prettyDate(from)} – ${prettyDate(to)}`;

export default function Dashboard() {
  const assistantId = useSelector((state) => state.assistants.activeId);
  const dispatch = useDispatch();

  const [days, setDays] = useState(RANGES[0].days);
  const [rangeOpen, setRangeOpen] = useState(false);
  const [overview, setOverview] = useState(null);
  // The chart and topics have their own endpoints: /overview carries the four
  // headline metrics only, so reading them from it left both cards empty.
  const [series, setSeries] = useState([]);
  const [topics, setTopics] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const rangeRef = useRef(null);

  const range = rangeFor(days);
  const { from, to } = range;

  useEffect(() => {
    if (!rangeOpen) return undefined;

    const onPointerDown = (event) => {
      if (!rangeRef.current?.contains(event.target)) setRangeOpen(false);
    };

    document.addEventListener("mousedown", onPointerDown);
    return () => document.removeEventListener("mousedown", onPointerDown);
  }, [rangeOpen]);

  // Depends on the date strings rather than the object so a re-render with an
  // identical window doesn't refetch.
  const load = useCallback(async () => {
    if (!assistantId) return;

    setLoading(true);
    setError("");

    const [metrics, trend, topicsResult, conversations] = await Promise.all([
      dispatch(getAssistantOverview(assistantId, { from, to })),
      dispatch(getConversationTimeseries(assistantId, { from, to })),
      dispatch(getConversationTopics(assistantId, { from, to })),
      // Newest first, so one page of five is the recent list.
      dispatch(listConversations(assistantId, { page: 1, limit: 5 })),
    ]);

    setLoading(false);

    // Independent of the metrics: a failed overview shouldn't blank a chart
    // that loaded perfectly well.
    setSeries(readSeries(trend?.series));
    setTopics(readTopics(topicsResult?.topics));
    setRecent(
      mapConversations(conversations?.conversations).map((item) => ({
        id: item.id,
        name: item.name,
        detail: item.preview,
        time: item.updatedAt,
        status: item.status,
      })),
    );

    if (!metrics?.success) {
      // A stale assistant id has already been cleared by the action; the
      // sidebar will select a valid one and this reloads. Showing "not found"
      // in the meantime would just be noise.
      setOverview(null);
      if (!metrics?.notFound) {
        setError(metrics?.message || "We couldn't load your dashboard.");
      }
      return;
    }

    setOverview(mapOverview(metrics.overview));
  }, [assistantId, from, to, dispatch]);

  useEffect(() => {
    load();
  }, [load]);

  const renderBody = () => {
    if (!assistantId) {
      return (
        <div className="asst-list-state">
          <span className="asst-list-empty-icon">
            <RobotIcon />
          </span>
          <strong>No assistant selected</strong>
          <p>Pick one from the switcher at the top of the sidebar.</p>
        </div>
      );
    }

    if (loading && !overview) {
      return (
        <LoadingState
          title="Loading your dashboard"
          detail="Fetching your assistants and this week's activity."
        />
      );
    }

    return (
      <>
        {error && <p className="auth-error">{error}</p>}

        {/* The response carries previousValue and changePct but no dates for
            the window they came from, so naming one here would be a guess
            presented as fact. */}
        <StatCards
          overview={overview}
          comparedTo="previous period"
        />

        <div className="dash-grid-primary">
          <ConversationsChart series={series} loading={loading} />
          <AssistantLiveCard />
        </div>

        <div className="dash-grid-secondary">
          <TopTopics topics={topics} />
          <RecentConversations
            conversations={recent}
            assistantId={assistantId}
            loading={loading}
          />
          <QuickActions />
        </div>

        {/* <ImproveBanner /> */}
      </>
    );
  };

  return (
    <div className="dash">
      <Sidebar />

      <div className="dash-main">
        <Topbar>
          <div className="dash-range-dropdown" ref={rangeRef}>
            <button
              type="button"
              className="dash-topbar__range"
              onClick={() => setRangeOpen((open) => !open)}
              aria-expanded={rangeOpen}
            >
              <CalendarIcon />
              {prettyRange(range)}
              <ChevronDownIcon />
            </button>

            {rangeOpen && (
              <ul className="dash-range-menu">
                {RANGES.map((option) => (
                  <li key={option.days}>
                    <button
                      type="button"
                      className={option.days === days ? "is-active" : ""}
                      onClick={() => {
                        setDays(option.days);
                        setRangeOpen(false);
                      }}
                    >
                      {option.label}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {/* <button type="button" className="dash-topbar__share">
            <ShareIcon />
            Share
          </button> */}
        </Topbar>

        <div className="dash-content">{renderBody()}</div>
      </div>
    </div>
  );
}
